import type {
  IShoppingCartContext,
  PreferredShippingMethod,
} from '@monofront/types';
import { useMemo } from 'react';
import {
  DeliveryBarMachineContext,
  ErrorTypes,
  formatShippingMethod,
  STATES_ENUM,
} from '../contexts/DeliveryBarContext';
import {
  validateDeliveryBarState,
  type DeliveryBarValidationResultInvalid,
} from '../contexts/deliveryBar/deliveryBarFormValidator';
import { machineValueSelector } from '../stateMachines/deliveryBar/selectors';
import { matchShippingMethodToEligible } from '../utils/shippingMethodMatcher';
import {
  getIsShippingFreshMethodEligible,
  getIsShippingUltraFreshMethodEligible,
} from './useDeliveryMethodEligibility';
import { useFetchEligibleShippingMethods } from './usePreferredShippingMethodWithConfigurations';

export type DeliveryBarError = {
  errorAtState: STATES_ENUM;
  errorType: ErrorTypes;
} | null;

/**
 * Derives the errors from cart state and validator function.
 * Uses customer data (authenticated or local storage).
 * The error is computed reactively whenever the underlying data changes.
 */

type UseDeliveryBarErrorParams = {
  shoppingCart: IShoppingCartContext['shoppingCart'];
  customer?: {
    address?: {
      street: string;
      city: string;
      postalCode: string;
      country: string;
    } | null;
    preferredShippingMethod?: PreferredShippingMethod | null;
  } | null;
  waitingProduct?: { tags: string[] } | null;
};

export const useDeliveryBarError = ({
  customer,
  shoppingCart,
  waitingProduct,
}: UseDeliveryBarErrorParams) => {
  const machineStateValue =
    DeliveryBarMachineContext.useSelector(machineValueSelector);

  const eligibleShippingMethods = useFetchEligibleShippingMethods(
    customer?.address,
  );

  const error = useMemo<DeliveryBarError>(() => {
    // Format customer data to machine format
    const address = customer?.address ?? null;
    const shippingMethod = customer?.preferredShippingMethod
      ? formatShippingMethod(customer.preferredShippingMethod)
      : null;

    // Only compute error if we have the necessary data
    if (!shippingMethod || !address) {
      return null;
    }

    // Match shipping method to eligible methods to get configurations
    const shippingMethodWithConfigurations = matchShippingMethodToEligible(
      shippingMethod,
      eligibleShippingMethods,
    );

    // If shopping cart is not loaded yet, don't compute error
    // The validator will return isValid: true for null cart, but we should
    // avoid unnecessary validation calls during initial load
    if (!shoppingCart) {
      return null;
    }

    // Only validate if we have a shipping method with configurations
    // (we need configurations to properly validate eligibility)
    if (
      !shippingMethodWithConfigurations ||
      !shippingMethodWithConfigurations.configurations ||
      shippingMethodWithConfigurations.configurations.length === 0
    ) {
      return null;
    }

    const validationResult = validateDeliveryBarState({
      selectedShippingMethod: shippingMethodWithConfigurations,
      shoppingCart: shoppingCart,
      isShippingFreshMethodEligible: getIsShippingFreshMethodEligible(
        shippingMethodWithConfigurations,
      ),
      isShippingUltraFreshMethodEligible: getIsShippingUltraFreshMethodEligible(
        shippingMethodWithConfigurations,
      ),
      waitingProduct,
    });

    if (validationResult.isValid) {
      return null;
    }

    return {
      errorAtState: machineStateValue,
      errorType: (validationResult as DeliveryBarValidationResultInvalid).error
        .errorType,
    };
  }, [
    shoppingCart,
    customer?.address,
    customer?.preferredShippingMethod,
    eligibleShippingMethods,
    machineStateValue,
    waitingProduct,
  ]);

  return {
    error,
  };
};
